import {type ApolloError} from '../../state/graphql/apolloWithAbort';
import {checkIfTeamMemberOrAdmin} from '../Team/utils';
import {
  type EntityPageQuery,
  type Member,
  useEntityPageQuery,
  useProfileUserCheckQuery,
} from './../../generated/graphql';
import {useViewer} from './../../state/viewer/hooks';

export type EntityPageData = {
  initialLoading: boolean;
  isTeamMemberOrAdmin: boolean;
  data: EntityPageQuery[`entity`];
  error: ApolloError | undefined;
  loading: boolean;
  isViewerLoggedIn: boolean;
  isEntityNameAlsoUsername: boolean;
  /**
   * True while the user query is still loading. Used to prevent rendering
   * NoMatch prematurely when entity doesn't exist but user might.
   */
  isUserQueryLoading: boolean;
};

export enum EntityType {
  Organization = 'organization',
  Team = 'team',
  Personal = 'personal',
}

export function useEntityPageData(entityName: string): EntityPageData {
  const viewer = useViewer();
  const {data, error, loading} = useEntityPageQuery({
    variables: {entityName},
  });

  const {data: userData, loading: userLoading} = useProfileUserCheckQuery({
    variables: {username: entityName},
  });

  const {members = []} = data?.entity || {};
  const isTeamMemberOrAdmin = checkIfTeamMemberOrAdmin(
    viewer,
    members as Partial<Member[]>
  );

  const initialLoading = loading && !data;

  const isViewerLoggedIn = viewer != null;

  const isEntityNameAlsoUsername = !userLoading && userData?.user != null;

  return {
    initialLoading,
    isTeamMemberOrAdmin,
    isViewerLoggedIn,
    data: data?.entity,
    error,
    loading,
    isEntityNameAlsoUsername,
    isUserQueryLoading: userLoading,
  };
}
